/**
 * 날짜 포맷팅을 위한 컴포저블
 */
export function useDateFormatter() {
  /**
   * 날짜를 한국어 형식으로 포맷팅
   * @param {string|Date} dateInput - 날짜 문자열 또는 Date 객체
   * @param {Object} options - 포맷팅 옵션
   * @returns {string} 포맷팅된 날짜 문자열
   */
  const formatDate = (dateInput, options = {}) => {
    if (!dateInput) return '-';
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '-';
    }

    const defaultOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...options,
    };

    return date.toLocaleDateString('ko-KR', defaultOptions);
  };

  /**
   * 날짜를 시간 포함하여 포맷팅
   * @param {string|Date} dateInput - 날짜 문자열 또는 Date 객체
   * @returns {string} 포맷팅된 날짜시간 문자열
   */
  const formatDateTime = (dateInput) => {
    if (!dateInput) return '-';
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '-';
    }

    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  /**
   * 상대적 시간 표시 (예: "3일 전", "2시간 전")
   * @param {string|Date} dateInput - 날짜 문자열 또는 Date 객체
   * @returns {string} 상대적 시간 문자열
   */
  const formatRelativeTime = (dateInput) => {
    if (!dateInput) return '-';
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '-';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return '방금 전';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}분 전`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}시간 전`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}일 전`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months}개월 전`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years}년 전`;
    }
  };

  /**
   * YYYY-MM-DD 형식으로 포맷팅
   * @param {string|Date} dateInput - 날짜 문자열 또는 Date 객체
   * @returns {string} YYYY-MM-DD 형식의 날짜 문자열
   */
  const formatDateISO = (dateInput) => {
    if (!dateInput) return '';
    
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '';
    }

    return date.toISOString().split('T')[0];
  };

  /**
   * 두 날짜 사이의 일수 계산
   * @param {string|Date} startDate - 시작 날짜
   * @param {string|Date} endDate - 종료 날짜
   * @returns {number} 일수 차이
   */
  const getDaysBetween = (startDate, endDate) => {
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Invalid date input:', { startDate, endDate });
      return 0;
    }

    const diffInTime = end.getTime() - start.getTime();
    return Math.ceil(diffInTime / (1000 * 3600 * 24));
  };

  return {
    formatDate,
    formatDateTime,
    formatRelativeTime,
    formatDateISO,
    getDaysBetween,
  };
} 